// Microbe RTS — PvP session: the public net API the lobby / main.ts wires into.
//
// Usage (lobby):
//   const engine = detectEngine();
//   const transport = connectMatch(code, { engine });        // opens the WS
//   transport.onStart((seed, side) => {
//     const sim = createSim({ seed });                        // both clients: SAME seed
//     const session = createPvpSession(sim, transport);       // side comes from the transport
//     // In PvP mode the UI calls session.submitCommand(cmd) INSTEAD of sim.command(cmd);
//     // a 30 Hz driver calls session.step() each frame (it gates on inputs, applies both sides'
//     // commands in canonical order, ticks the sim, and runs the checksum exchange).
//   });
//
// createPvpSession must be called AFTER 'start' has fired (so transport.side is known). It only wires
// transport events into a Lockstep scheduler — all determinism/ordering logic lives there.

import type { Command, Side, Sim } from '../types.js';
import type { Transport } from './transport.js';
import type { EndReason } from './protocol.js';
import { Lockstep } from './lockstep.js';

// Re-export the lobby's entry points so main.ts can pull the whole PvP surface from one module.
export { detectEngine } from './engine.js';
export { connectMatch, makeCode } from './transport.js';
export type { Transport, ConnectOpts } from './transport.js';

export interface PvpSession {
  /** In PvP mode, call this INSTEAD of sim.command(cmd). Delayed by D ticks and synced to the peer. */
  submitCommand(cmd: Command): void;
  /** Advance one lockstep tick if both inputs are ready. Returns true iff the sim ticked. */
  step(): boolean;
  /** This client's side. */
  readonly side: Side;
  /** Ticks executed so far. */
  readonly currentTick: number;
  /** Register a match-end callback: 'desync' | 'peer_gone' | 'engine_mismatch' | 'error'. */
  onEnd(cb: (reason: EndReason) => void): void;
}

export function createPvpSession(sim: Sim, transport: Transport): PvpSession {
  const side = transport.side;
  if (side == null) {
    throw new Error('createPvpSession: transport has no side yet — call it after onStart(seed, side) fires');
  }

  const ls = new Lockstep(sim, side, transport);

  // Peer-relayed frames feed the scheduler.
  transport.onInput((m) => ls.receiveInput(m));
  transport.onSum((m) => ls.receiveSum(m));
  transport.onDesync(() => ls.end('desync'));

  // Control frames / socket faults end the match.
  transport.onPeerGone(() => ls.end('peer_gone'));
  transport.onEngineMismatch(() => ls.end('engine_mismatch'));
  transport.onError(() => ls.end('error'));

  return {
    submitCommand: (cmd) => ls.submitCommand(cmd),
    step: () => ls.step(),
    get side() {
      return ls.side;
    },
    get currentTick() {
      return ls.currentTick;
    },
    onEnd: (cb) => ls.onEnd(cb),
  };
}
